import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useToggler } from '@cbhq/cds-common';
import { Button } from '@cbhq/cds-web/buttons';
import { SelectOption } from '@cbhq/cds-web/controls';
import { Dropdown } from '@cbhq/cds-web/dropdown';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, VStack } from '@cbhq/cds-web/layout';
import { Pressable, ThemeProvider } from '@cbhq/cds-web/system';
import { TextHeadline, TextTitle3, TextTitle4 } from '@cbhq/cds-web/typography';

import { useProject } from ':cloud/contexts/ProjectContext';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { useGetProjects } from ':cloud/queries/Projects/useGetProjects';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { Project } from ':cloud/types/ts_types';
import { ProjectCreateModal } from ':cloud/widgets/projects/ProjectCreateModal';
import { ProjectSelectorOption } from ':cloud/widgets/projects/ProjectSelectorOption';
import { ProjectSettingsModal } from ':cloud/widgets/projects/ProjectSettingsModal';

const PROJECT_SELECTOR_CELL_HEIGHT = 58;
const PROJECT_SELECTOR_MIN_WIDTH = 310;
const PROJECT_SELECTOR_MIN_WIDTH_NARROW = 210;
const PROJECT_SELECTOR_LABEL_MAX_WIDTH = '28vw';

type ProjectSelectorProps = {
  compact?: boolean;
};

export function ProjectSelector({ compact }: ProjectSelectorProps) {
  const { isDesktop } = useSimpleBreakpoints();
  const [isSettingsModalVisible, { toggleOn: showSettingsModal, toggleOff: hideSettingsModal }] =
    useToggler(false);
  const [isCreateModalVisible, { toggleOn: showCreateModal, toggleOff: hideCreateModal }] =
    useToggler(false);
  const [isDropdownOpen, { toggle: toggleDropdown }] = useToggler(false);
  const { selectedProject } = useProject();
  const history = useHistory();
  const location = useLocation();
  const { activeOrg } = useGetUser();
  const { projects } = useGetProjects(activeOrg?.organizationId || '');

  const handleSelectChange = useCallback(
    (newValue) => {
      if (newValue === 'create') {
        showCreateModal();
        return;
      }
      if (newValue === selectedProject?.id) {
        showSettingsModal();
        return;
      }
      const newSelection = projects.find((project) => project.id === newValue);
      if (newSelection) {
        history.push(`${location.pathname}?projectId=${newSelection.id}`);
      }
    },
    [history, projects, selectedProject, showSettingsModal, showCreateModal, location.pathname],
  );

  const content = useMemo(
    () => (
      <VStack position="relative">
        {projects.map((project) => (
          <ProjectSelectorOption
            key={project.id}
            project={project}
            minHeight={PROJECT_SELECTOR_CELL_HEIGHT}
          />
        ))}
        {projects.length === 0 && (
          <SelectOption
            value="no-projects"
            title={
              <TextHeadline as="p" overflow="wrap" align="center">
                You don&apos;t have any projects yet. Create one below.
              </TextHeadline>
            }
            disabled
            compact
            minHeight={PROJECT_SELECTOR_CELL_HEIGHT * 2}
          />
        )}
        <VStack
          position="sticky"
          bottom={0}
          left={0}
          height={PROJECT_SELECTOR_CELL_HEIGHT}
          overflow="hidden"
        >
          <SelectOption
            value="create"
            key="create"
            accessory={
              <Box spacing={1} alignItems="center" justifyContent="center">
                <Icon size="xs" color="primary" name="add" />
              </Box>
            }
            title={
              <TextHeadline color="primary" as="p">
                Create new project
              </TextHeadline>
            }
            shouldOverflow={false}
            minHeight={PROJECT_SELECTOR_CELL_HEIGHT}
            testID="project-selector-create-option"
          />
        </VStack>
      </VStack>
    ),
    [projects],
  );

  return (
    <>
      <Dropdown
        value={selectedProject?.id}
        onChange={handleSelectChange}
        content={content}
        minWidth={isDesktop ? PROJECT_SELECTOR_MIN_WIDTH : PROJECT_SELECTOR_MIN_WIDTH_NARROW}
        onCloseMenu={toggleDropdown}
        onOpenMenu={toggleDropdown}
      >
        <VStack justifyContent="center">
          <ProjectSelectorButton
            compact={compact}
            selectedProject={selectedProject}
            isDropdownOpen={isDropdownOpen}
          />
        </VStack>
      </Dropdown>
      <ThemeProvider scale="small">
        <ProjectSettingsModal visible={isSettingsModalVisible} onRequestClose={hideSettingsModal} />
        <ProjectCreateModal visible={isCreateModalVisible} onRequestClose={hideCreateModal} />
      </ThemeProvider>
    </>
  );
}

type ProjectSelectorButtonProps = {
  compact?: boolean;
  selectedProject: Project | null;
  isDropdownOpen: boolean;
};

function ProjectSelectorButton({
  compact,
  selectedProject,
  isDropdownOpen,
}: ProjectSelectorButtonProps) {
  const endIcon = compact ? undefined : isDropdownOpen ? 'caretUp' : 'caretDown';

  if (compact) {
    return (
      <Pressable background="transparent" testID="project-selector-dropdown">
        <Box maxWidth={PROJECT_SELECTOR_LABEL_MAX_WIDTH}>
          <TextTitle4 as="h1" overflow="truncate" noWrap color="foregroundMuted">
            {selectedProject?.name || 'Create a project'}
          </TextTitle4>
        </Box>
      </Pressable>
    );
  }

  return (
    <Button
      variant="secondary"
      transparent
      endIcon={endIcon}
      flush="start"
      compact
      testID="project-selector-dropdown"
    >
      <Box maxWidth={PROJECT_SELECTOR_LABEL_MAX_WIDTH}>
        <TextTitle3 as="h1" overflow="truncate" noWrap>
          {selectedProject?.name || 'Create a project'}
        </TextTitle3>
      </Box>
    </Button>
  );
}
